import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

/**
 * The one transient notification in the app. Replaces three mechanisms that
 * were in use at once: native alert() (blocking browser chrome), react-toastify
 * (whose ToastContainer was only ever mounted in the unrouted ColorsLayer demo,
 * so every toast.* call in the app rendered nothing), and ad-hoc banners.
 *
 * notify.* is a module-level singleton rather than a hook so that non-component
 * code — axios interceptors, helpers — can raise a toast without a React tree.
 */

const ICONS = {
  success: "tabler:circle-check",
  error: "tabler:alert-triangle",
  info: "tabler:info-circle",
};

let emit = null;
let nextId = 1;

const push = (type, message, ms) => {
  if (!message) return;
  // Before <ToastHost /> mounts there is nowhere to render. Failing loudly in
  // the console beats the old silent no-op.
  if (!emit) {
    console.warn("[notify] ToastHost is not mounted:", message);
    return;
  }
  emit({ id: nextId++, type, message: String(message), ms });
};

export const notify = {
  success: (message, ms = 3500) => push("success", message, ms),
  error: (message, ms = 5000) => push("error", message, ms),
  info: (message, ms = 4000) => push("info", message, ms),
};

export const ToastHost = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    emit = (toast) => setToasts((list) => [...list, toast]);
    return () => {
      emit = null;
    };
  }, []);

  const dismiss = (id) => setToasts((list) => list.filter((t) => t.id !== id));

  if (toasts.length === 0) return null;

  return (
    <div className="ms-toasts" role="region" aria-label="Билдиришномалар">
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
};

const Toast = ({ type, message, ms, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, ms);
    return () => clearTimeout(timer);
  }, [ms, onDismiss]);

  return (
    <div
      className={`ms-toast ms-toast--${type}`}
      // Errors interrupt; success and info must not steal focus from a form.
      role={type === "error" ? "alert" : "status"}
    >
      <Icon icon={ICONS[type]} />
      <p>{message}</p>
      <button type="button" onClick={onDismiss} aria-label="Ёпиш">
        <Icon icon="tabler:x" />
      </button>
    </div>
  );
};

export default ToastHost;
